import { useCallback, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  appendText,
  askPath,
  readAskEvents,
  streamedAnswer,
  upsertStep,
  type AnswerConfidence,
  type AnswerCoverage,
  type AnswerStep,
  type AnswerTrail,
  type AnswerUnfinished,
  type AskStage,
  type ChatSource,
  type QuestionTopic,
  type StreamedText,
} from "@gb/contracts";
import { authorizedFetch } from "@/api/client";
import { answerStepsKey, requestCancel } from "./answer-steps-api";
import { chatKeys } from "./chat-api";

/**
 * Asking a question, and watching the answer arrive.
 *
 * `fetch` plus a stream reader rather than `EventSource`, for a reason that
 * looks like a detail and is not: `EventSource` cannot send an `Authorization`
 * header, and this API takes a bearer token. It also cannot POST. Reading the
 * body ourselves costs a small parser and keeps one auth path for the whole app.
 *
 * Every frame is parsed against `AskEventSchema` before it is believed. The
 * route is off the ts-rest contract, so this is the only place the wire shape is
 * checked — and a malformed frame silently ignored is better than one rendered
 * as `undefined` in the middle of an answer.
 *
 * **The answering agent's frames** (2026-09-26, Phase 5). A `step` frame comes
 * when a step starts and again when it finishes, with the same `seq`, and is
 * upserted by it. A `delta` carries the model turn that wrote it: a turn's
 * text is the answer until the server sends a text note of that turn, and
 * then it is that note (`streamedAnswer`). `done` carries the trail, the
 * coverage, how it stopped and the review's topic, so the screen needs no
 * refetch to show them.
 */

export type StreamState = {
  /** The text as it arrived, by turn. `answer` is read from it. */
  texts: StreamedText[];
  sources: ChatSource[];
  /** The answer's steps so far, by `seq`. */
  steps: AnswerStep[];
  stage: AskStage | null;
  /** The model's summarised reasoning, when it is thinking before answering. */
  reasoning: string;
  /**
   * How well the sources supported it, from the `done` frame.
   *
   * Only known at the end — the review runs against the finished answer — so
   * this stays null for the whole stream and lands with the last frame. Null
   * also survives a review that could not run, which is *not assessed* rather
   * than low.
   */
  confidence: AnswerConfidence | null;
  confidenceNote: string | null;
  /** From `done`, so the screen shows them before the stored answer is read back. */
  trail: AnswerTrail | null;
  coverage: AnswerCoverage | null;
  unfinished: AnswerUnfinished | null;
  topic: QuestionTopic | null;
  pending: boolean;
  error: string | null;
  /**
   * The question the error is about (2026-09-27). The error is kept when the
   * buffer is cleared, and it belongs under its own question only, not under
   * the next one opened.
   */
  errorFor: string | null;
  /**
   * The answer will not come: the server sent an `error` frame, or refused
   * the ask before the stream opened. Kept with the error. False for a
   * dropped connection, after which the answer runs on at the server (§1.11),
   * so the screen goes on waiting for it.
   */
  failed: boolean;
  /**
   * Which question this stream is answering.
   *
   * The stream is held above the Brain's tabs (`ask-stream.tsx`), so it
   * outlives the screen that asked: moving to another question, or to the
   * History tab and back, keeps the buffer. Without a tag on it a half-written
   * answer for one question renders underneath another one's title. The
   * caller compares this against the question it is showing.
   */
  askedFor: string | null;
};

const IDLE: StreamState = {
  texts: [],
  sources: [],
  steps: [],
  stage: null,
  reasoning: "",
  confidence: null,
  confidenceNote: null,
  trail: null,
  coverage: null,
  unfinished: null,
  topic: null,
  pending: false,
  error: null,
  errorFor: null,
  failed: false,
  askedFor: null,
};

/**
 * The stream, for one project. `ask` takes the question it answers, because
 * one stream serves every question: it is held above the Brain's tabs
 * (`ask-stream.tsx`), not by the screen showing a question.
 */
export function useChatStream(projectId: string) {
  const [state, setState] = useState<StreamState>(IDLE);
  const queryClient = useQueryClient();
  const inflight = useRef<{ controller: AbortController; threadId: string } | null>(null);
  // The steps as the last frame left them, for `finally`, which runs outside
  // any render and cannot read the state.
  const stepsSoFar = useRef<AnswerStep[]>([]);

  const reset = useCallback(() => setState(IDLE), []);

  const ask = useCallback(
    async (threadId: string, question: string): Promise<void> => {
      // One in flight at a time. A second question while the first is streaming
      // would interleave two answers into one buffer. And asking a second
      // question stops the first (`product/0005` §2.6): the answer now runs on
      // when the connection closes, so closing it is not enough, and the stop
      // is stored on the first question.
      const previous = inflight.current;
      if (previous) void requestCancel(projectId, previous.threadId);
      previous?.controller.abort();
      const controller = new AbortController();
      inflight.current = { controller, threadId };
      stepsSoFar.current = [];

      setState({
        ...IDLE,
        pending: true,
        stage: "thinking",
        askedFor: threadId,
      });

      try {
        const response = await authorizedFetch(askPath(projectId, threadId), {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ question }),
          signal: controller.signal,
        });

        if (!response.ok || !response.body) {
          // Errors before the stream opens are ordinary HTTP, because the server
          // checks ownership and validates the body before writing a byte.
          setState((prev) => ({
            ...prev,
            pending: false,
            error:
              response.status === 404
                ? "That conversation no longer exists."
                : "Couldn’t ask that question.",
            errorFor: threadId,
            failed: true,
          }));
          return;
        }

        // The server writes the user's turn *before* it starts choosing
        // sources, so by the time the first frame lands the question is already
        // a row. Refreshing here rather than only in `finally` is what puts it
        // in the history the moment it is asked instead of a minute later, when
        // the answer happens to finish.
        let announced = false;

        for await (const event of readAskEvents(response.body)) {
          switch (event.type) {
            case "status":
              if (!announced) {
                announced = true;
                void queryClient.invalidateQueries({
                  queryKey: chatKeys.allQuestions(projectId),
                });
              }
              setState((prev) => ({ ...prev, stage: event.stage }));
              break;
            case "sources":
              setState((prev) => ({ ...prev, sources: event.sources }));
              break;
            case "step":
              stepsSoFar.current = upsertStep(stepsSoFar.current, event.step);
              setState((prev) => ({ ...prev, steps: stepsSoFar.current }));
              break;
            case "reasoning":
              setState((prev) => ({
                ...prev,
                reasoning: prev.reasoning + event.text,
              }));
              break;
            case "delta":
              setState((prev) => ({
                ...prev,
                texts: appendText(prev.texts, event.turn ?? null, event.text),
              }));
              break;
            case "error":
              setState((prev) => ({
                ...prev,
                pending: false,
                error: event.message,
                errorFor: threadId,
                failed: true,
              }));
              break;
            case "done":
              setState((prev) => ({
                ...prev,
                pending: false,
                stage: null,
                confidence: event.confidence,
                confidenceNote: event.confidenceNote,
                trail: event.trail ?? null,
                coverage: event.coverage ?? null,
                unfinished: event.unfinished ?? null,
                topic: event.topic ?? null,
              }));
              break;
          }
        }
      } catch (error) {
        // An abort is the user asking something else or leaving. Not a failure,
        // and showing them an error for their own action reads as a bug.
        if (controller.signal.aborted) return;
        setState((prev) => ({
          ...prev,
          pending: false,
          error:
            error instanceof Error
              ? error.message
              : "The answer was interrupted.",
          errorFor: threadId,
        }));
      } finally {
        if (inflight.current?.controller === controller) {
          inflight.current = null;
          setState((prev) => ({ ...prev, pending: false }));

          // The streamed steps go into the steps query **before** the buffer is
          // cleared, so the list does not blank for a beat while the stored
          // copy is read back (§3 of the design).
          if (stepsSoFar.current.length > 0) {
            queryClient.setQueryData(answerStepsKey(projectId, threadId), stepsSoFar.current);
          }

          // **Awaited, then cleared** — and the order is the whole point. The
          // screen renders the stored turns *plus* whatever is streaming, so
          // clearing the buffer before the refetch lands blanks the answer for
          // a beat, and never clearing it shows the answer twice: once from the
          // buffer and once from the row that was just written.
          //
          // The stored copy is also the corrected one — dead citation markers
          // are stripped before the row is written — so this is a
          // reconciliation, not only a tidy-up. The thread's key is the steps'
          // prefix, so the stored steps are read back with it.
          await queryClient.invalidateQueries({
            queryKey: chatKeys.thread(projectId, threadId),
          });
          setState((prev) =>
            prev.error ? { ...IDLE, error: prev.error, errorFor: prev.errorFor, failed: prev.failed } : IDLE,
          );
          void queryClient.invalidateQueries({
            queryKey: chatKeys.allThreads(projectId),
          });
          // The History tab lists the question that just got its answer, so a
          // stale list would show it as unanswered — or omit it entirely.
          void queryClient.invalidateQueries({
            queryKey: chatKeys.allQuestions(projectId),
          });
        }
      }
    },
    [projectId, queryClient],
  );

  const cancel = useCallback(() => {
    // **Stop is stored first, then the stream is closed** (2026-09-26, Phase
    // 5). The answer runs on when the connection closes, so closing it no
    // longer stops it; the stop is written on the question, and the answer
    // stops at its next step. Closing the stream still stops an answer on the
    // old path, which stops at a closed connection.
    //
    // The ref is deliberately **not** cleared here. `ask`'s `finally` checks
    // `inflight.current?.controller === controller` before it reconciles, so
    // clearing it first made that check false and skipped the invalidation
    // entirely — leaving the screen rendering its in-memory partial answer
    // forever, never reconciled against the row that was actually stored.
    // Aborting is enough; `finally` clears the ref itself.
    const running = inflight.current;
    if (running) void requestCancel(projectId, running.threadId);
    running?.controller.abort();
    setState((prev) => ({ ...prev, pending: false }));
  }, [projectId]);

  // **Deliberately no abort on unmount.** This used to cancel the request when
  // the screen went away, on the reasoning that an answer nobody reads is an
  // answer nobody should pay for. That reasoning is wrong now that a question
  // is an addressable record listed in the history: leaving the Ask tab meant
  // the server saw a client disconnect, marked the answer incomplete and stored
  // it with "This answer stopped early" — so a tab switch permanently truncated
  // work the reader fully intended to come back to.
  //
  // Fire and forget instead. The fetch keeps running, which is what keeps the
  // connection open and lets the server finish and persist; `finally` still
  // invalidates, so whichever screen is showing that question picks the answer
  // up. React 18 no-ops `setState` on an unmounted component, so the loop
  // draining into nothing is harmless.
  //
  // Unmounting is rarer since 2026-09-27: the hook is held above the Brain's
  // tabs (`ask-stream.tsx`), so only leaving the Brain, or the project,
  // unmounts it.
  //
  // **Stop still stops.** `cancel()` stores the stop and closes the stream,
  // and starting another question does the same to the one in flight — those
  // are a person choosing to end it, which is exactly when a truncated record
  // is the honest outcome.

  const answer = useMemo(() => streamedAnswer(state.texts, state.steps), [state.texts, state.steps]);

  return { ...state, answer, ask, cancel, reset };
}
